import { ReactNode } from 'react';
import {
  Box, TextField, InputAdornment, IconButton, Tooltip, Typography,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { Search, Clear, Visibility, VisibilityOff } from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';

export interface ToolbarAction {
  icon: SvgIconComponent;
  hint: string;
  onClick: () => void;
  disabled?: boolean;
  color?: 'inherit' | 'primary' | 'warning' | 'success' | 'error';
}

interface Props {
  search: string;
  onSearch: (v: string) => void;
  filters?: ReactNode;
  /** Status values to show; nothing selected means no status filter. */
  statuses?: { value: string[]; onChange: (v: string[]) => void };
  actions?: ToolbarAction[];
  selectedCount?: number;
  selectionActions?: ToolbarAction[];
  pluralName?: string;
  singularName?: string;
}

function ActionIcons({ actions }: { actions: ToolbarAction[] }) {
  return (
    <>
      {actions.map(({ icon: Icon, hint, onClick, disabled, color }) => (
        <Tooltip key={hint} title={hint}>
          <span>
            <IconButton size="small" aria-label={hint} onClick={onClick} disabled={disabled} color={color || 'default'}>
              <Icon fontSize="small" aria-hidden />
            </IconButton>
          </span>
        </Tooltip>
      ))}
    </>
  );
}

export default function EntityGridToolbar({
  search, onSearch, filters, statuses, actions = [],
  selectedCount = 0, selectionActions = [], pluralName = 'items',
  singularName = pluralName.replace(/s$/, ''),
}: Props) {
  if (selectedCount > 0 && selectionActions.length) {
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 1, px: 1.5, py: 0.75, bgcolor: 'primary.main', color: 'primary.contrastText',
      }}>
        <Typography variant="body2">{selectedCount} {selectedCount === 1 ? singularName : pluralName} selected</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'inherit' }}>
          <ActionIcons actions={selectionActions} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 1, px: 1.5, py: 0.75, flexWrap: 'wrap',
    }}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <TextField
          variant="standard"
          size="small"
          placeholder="Search…"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          sx={{ minWidth: 220 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
            endAdornment: (
              <InputAdornment position="end" sx={{ visibility: search ? 'visible' : 'hidden' }}>
                <IconButton size="small" aria-label="Clear search" onClick={() => onSearch('')}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {filters}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {statuses && (
          <ToggleButtonGroup
            size="small"
            aria-label="Status"
            value={statuses.value}
            onChange={(_e, next: string[]) => statuses.onChange(next)}
          >
            <Tooltip title="Active">
              <ToggleButton value="active" sx={{ p: 0.5 }} aria-label="Active">
                <Visibility sx={{ fontSize: 18 }} />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Archived">
              <ToggleButton value="archived" sx={{ p: 0.5 }} aria-label="Archived">
                <VisibilityOff sx={{ fontSize: 18 }} />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        )}
        <ActionIcons actions={actions} />
      </Box>
    </Box>
  );
}
